import React, { useState } from 'react';

const MbbRadarChart = ({ playerData }) => {
  // Create player statistics from the provided data
  const playerStats = [
    {
      metric: 'Shooting',
      value: playerData?.threePointPct && playerData?.twoPointPct
        ? (playerData.threePointPct + playerData.twoPointPct) / 2
        : 0,
      max: 70
    },
    {
      metric: 'Rebounding',
      value: playerData?.rebPct || 0,
      max: 25
    },
    {
      metric: 'Playmaking',
      value: playerData?.assistRate || 0,
      max: 15
    },
    {
      metric: 'Free Throws',
      value: playerData?.ftPct || 0,
      max: 100
    },
    {
      metric: 'Scoring',
      value: playerData?.scoring || 0,
      max: 25
    },
    {
      metric: 'Defense',
      value: playerData?.stealRate && playerData?.blockPct
        ? (playerData.stealRate + playerData.blockPct) * 5
        : 0,
      max: 15
    }
  ];

  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Chart dimensions - adjusted size to accommodate labels
  const size = 320;
  const center = size / 2;
  const radius = 80;
  const levels = 5;

  // Calculate angles for each metric (starting from top, going clockwise)
  const angleStep = (2 * Math.PI) / playerStats.length;

  // Generate polygon points
  const generatePolygonPoints = (data) => {
    return data.map((stat, index) => {
      const angle = (index * angleStep) - (Math.PI / 2); // Start from top
      const normalizedValue = Math.min(1, Math.max(0, stat.value / stat.max));
      const distance = radius * normalizedValue;

      return {
        x: center + Math.cos(angle) * distance,
        y: center + Math.sin(angle) * distance,
        angle: angle,
        ...stat
      };
    });
  };

  // Generate grid lines (concentric polygons)
  const generateGridLines = () => {
    const gridLines = [];

    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const points = playerStats.map((_, index) => {
        const angle = (index * angleStep) - (Math.PI / 2);
        return {
          x: center + Math.cos(angle) * levelRadius,
          y: center + Math.sin(angle) * levelRadius
        };
      });

      gridLines.push(points);
    }

    return gridLines;
  };

  // Generate axis lines
  const generateAxisLines = () => {
    return playerStats.map((stat, index) => {
      const angle = (index * angleStep) - (Math.PI / 2);
      return {
        x1: center,
        y1: center,
        x2: center + Math.cos(angle) * radius,
        y2: center + Math.sin(angle) * radius,
        stat: stat
      };
    });
  };

  // Generate label positions
  const generateLabels = () => {
    return playerStats.map((stat, index) => {
      const angle = (index * angleStep) - (Math.PI / 2);
      const labelDistance = radius + 20; // Reduced label distance

      return {
        x: center + Math.cos(angle) * labelDistance,
        y: center + Math.sin(angle) * labelDistance,
        stat: stat,
        angle: angle
      };
    });
  };

  const polygonPoints = generatePolygonPoints(playerStats);
  const gridLines = generateGridLines();
  const axisLines = generateAxisLines();
  const labels = generateLabels();

  // Convert points to SVG path
  const polygonPath = polygonPoints.map((point, index) =>
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ') + ' Z';

  const gridPaths = gridLines.map(line =>
    line.map((point, index) =>
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ') + ' Z'
  );

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="relative">
        <svg width={size} height={size}>
          {/* Grid lines */}
          {gridPaths.map((path, index) => (
            <path
              key={`grid-${index}`}
              d={path}
              fill="none"
              stroke="#374151"
              strokeWidth="1"
              opacity="0.3"
            />
          ))}

          {/* Axis lines */}
          {axisLines.map((line, index) => (
            <line
              key={`axis-${index}`}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke="#4b5563"
              strokeWidth="1"
              opacity="0.5"
            />
          ))}

          {/* Data polygon */}
          <path
            d={polygonPath}
            fill="rgba(59, 130, 246, 0.2)"
            stroke="#3b82f6"
            strokeWidth="2"
            className="drop-shadow-lg"
          />

          {/* Data points */}
          {polygonPoints.map((point, index) => (
            <circle
              key={`point-${index}`}
              cx={point.x}
              cy={point.y}
              r={hoveredPoint === index ? 6 : 4}
              fill="#3b82f6"
              stroke="BLACK"
              strokeWidth="2"
              className="cursor-pointer transition-all duration-200"
              onMouseEnter={() => setHoveredPoint(index)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
          ))}

          {/* Labels */}
          {labels.map((label, index) => (
            <text
              key={`label-${index}`}
              x={label.x}
              y={label.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-medium text-black"
              style={{
                textAnchor: label.angle > Math.PI / 2 || label.angle < -Math.PI / 2 ? 'end' : 'start'
              }}
            >
              {label.stat.metric}
            </text>
          ))}

          {/* Center dot */}
          <circle
            cx={center}
            cy={center}
            r="2"
            fill="#64748b"
          />
        </svg>

        {/* Hover tooltip */}
        {hoveredPoint !== null && (
          <div className="absolute top-2 left-2 bg-slate-700 text-white p-2 rounded-lg shadow-lg border border-slate-600 text-xs">
            <h4 className="font-semibold">{polygonPoints[hoveredPoint].metric}</h4>
            <p className="text-sm font-bold text-blue-400">
              {polygonPoints[hoveredPoint].value.toFixed(1)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MbbRadarChart